/**
 * ReklamasjonDialog - Registrer reklamasjon for en mangel
 * Sporing av reklamasjoner med svar og oppfølging
 */

import React, { useState } from 'react';
import crmApi from '../api/crm';
import './ReklamasjonDialog.css';

export default function ReklamasjonDialog({ sakId, mangelId, onClose }) {
  const [loading, setLoading] = useState(false);

  const [reklamasjonData, setReklamasjonData] = useState({
    reklamasjonsdato: new Date().toISOString().split('T')[0], // I dag
    mottaker: '',
    metode: 'epost',
    sporingsinfo: '',
    oppfolgingstiltak: '',
    neste_frist: '',
    notater: ''
  });

  const metoder = [
    { value: 'epost', label: 'E-post' },
    { value: 'rekommendert_brev', label: 'Rekommendert brev' },
    { value: 'digital_postkasse', label: 'Digital postkasse' },
    { value: 'fysisk_mote', label: 'Fysisk møte' },
    { value: 'telefon', label: 'Telefon' },
    { value: 'sms', label: 'SMS' },
    { value: 'annet', label: 'Annet' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validering
    if (!reklamasjonData.mottaker || !reklamasjonData.reklamasjonsdato) {
      alert('Mottaker og reklamasjonsdato er påkrevd');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        sak_id: sakId,
        mangel_id: mangelId,
        ...reklamasjonData
      };

      await crmApi.request('/reklamasjoner', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      alert('✓ Reklamasjon registrert');
      onClose();
    } catch (error) {
      console.error('Feil ved registrering av reklamasjon:', error);
      alert('Kunne ikke registrere reklamasjon: ' + (error.message || 'Ukjent feil'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-innhold reklamasjon-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📨 Ny reklamasjon</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="reklamasjon-form">
          {/* Reklamasjonsdato */}
          <div className="form-gruppe">
            <label htmlFor="reklamasjonsdato">
              Reklamasjonsdato <span className="required">*</span>
            </label>
            <input
              type="date"
              id="reklamasjonsdato"
              value={reklamasjonData.reklamasjonsdato}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, reklamasjonsdato: e.target.value })}
              required
            />
          </div>

          {/* Mottaker */}
          <div className="form-gruppe">
            <label htmlFor="mottaker">
              Mottaker <span className="required">*</span>
            </label>
            <input
              type="text"
              id="mottaker"
              placeholder="F.eks. 'Selger', 'Selgers advokat', 'Forsikringsselskap'"
              value={reklamasjonData.mottaker}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, mottaker: e.target.value })}
              required
            />
          </div>

          {/* Metode */}
          <div className="form-gruppe">
            <label htmlFor="metode">
              Metode <span className="required">*</span>
            </label>
            <select
              id="metode"
              value={reklamasjonData.metode}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, metode: e.target.value })}
              required
            >
              {metoder.map(metode => (
                <option key={metode.value} value={metode.value}>{metode.label}</option>
              ))}
            </select>
          </div>

          {/* Sporingsinfo */}
          <div className="form-gruppe">
            <label htmlFor="sporingsinfo">
              Sporingsinfo
              <small> (sporingsnummer, e-postreferanse, etc.)</small>
            </label>
            <input
              type="text"
              id="sporingsinfo"
              placeholder="F.eks. sporingsnummer, sendingsnummer, e-post ID"
              value={reklamasjonData.sporingsinfo}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, sporingsinfo: e.target.value })}
            />
          </div>

          {/* Oppfølgingstiltak */}
          <div className="form-gruppe">
            <label htmlFor="oppfolgingstiltak">Oppfølgingstiltak</label>
            <select
              id="oppfolgingstiltak"
              value={reklamasjonData.oppfolgingstiltak}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, oppfolgingstiltak: e.target.value })}
            >
              <option value="">Velg tiltak...</option>
              <option value="Avventer svar">Avventer svar</option>
              <option value="Sendt påminnelse">Sendt påminnelse</option>
              <option value="Juridiske skritt">Juridiske skritt</option>
              <option value="Forliksforhandlinger">Forliksforhandlinger</option>
              <option value="Avsluttet">Avsluttet</option>
            </select>
          </div>

          {/* Neste frist */}
          <div className="form-gruppe">
            <label htmlFor="neste_frist">
              Neste frist
              <small> (f.eks. frist for svar)</small>
            </label>
            <input
              type="date"
              id="neste_frist"
              value={reklamasjonData.neste_frist}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, neste_frist: e.target.value })}
            />
          </div>

          {/* Notater */}
          <div className="form-gruppe">
            <label htmlFor="notater">Notater</label>
            <textarea
              id="notater"
              rows="4"
              placeholder="Ytterligere notater om reklamasjonen..."
              value={reklamasjonData.notater}
              onChange={(e) => setReklamasjonData({ ...reklamasjonData, notater: e.target.value })}
            />
          </div>

          {/* Informasjon */}
          <div className="info-boks">
            <h4>ℹ️ Tips:</h4>
            <ul>
              <li>Lagre alltid bevis på reklamasjonen (e-post, kvittering, etc.)</li>
              <li>Noter sporingsnummer for rekommenderte forsendelser</li>
              <li>Sett en påminnelse hvis du ikke får svar innen fristen</li>
            </ul>
          </div>

          {/* Handlinger */}
          <div className="dialog-handlinger">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Registrerer...' : 'Registrer reklamasjon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
